import { useCallback, useEffect, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import { api, type Mystery } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Page } from '../components/ui'
import { ErrorScreen, Loading, Empty } from '../components/States'
import { LetterKeyboard, MARK_COLORS, type KeyMark } from '../components/LetterKeyboard'
import { Mascot } from '../components/Mascot'
import { Trace } from '../lib/trace'
import { say } from '../lib/speech'
import { sfx } from '../lib/sound'

/**
 * Mystery word: find one of your own words, letter by letter, in six tries.
 *
 * Wordle's shape, from research/word-games.md, with three changes for
 * children and for this app's rules:
 *
 *   * **No streak and no score.** Wordle's streak is the part that turns a
 *     puzzle into a duty (D-049). Finishing in two tries or six earns the same
 *     thing: the word, its meaning, and the next puzzle.
 *   * **Any letters count as a guess.** Wordle refuses guesses that are not
 *     in its dictionary. This app's catalogue is not a dictionary, and being
 *     told a real word "is not a word" is worse for a seven-year-old than an
 *     easier puzzle.
 *   * **Clues.** The meaning is one tap away, and shows by itself after three
 *     misses. Then the example sentence with the word blanked out. Finding a
 *     word from its meaning is pulling it out of memory, which is the part
 *     that teaches.
 *
 * Nothing here is saved (D-063). It cannot grow the garden.
 */

export function score(guess: string, answer: string): KeyMark[] {
  const marks: KeyMark[] = Array.from({ length: answer.length }, () => 'out')
  // Letters of the answer not matched exactly, so a doubled letter in the
  // guess is only marked "near" as many times as the answer actually has it.
  const left: Record<string, number> = {}
  for (let i = 0; i < answer.length; i++) {
    if (guess[i] === answer[i]) marks[i] = 'right'
    else left[answer[i]!] = (left[answer[i]!] ?? 0) + 1
  }
  for (let i = 0; i < answer.length; i++) {
    const g = guess[i]!
    if (marks[i] !== 'right' && (left[g] ?? 0) > 0) {
      marks[i] = 'near'
      left[g]! -= 1
    }
  }
  return marks
}

const RANK: Record<KeyMark, number> = { out: 0, near: 1, right: 2 }

export function MysteryWord({
  learnerId, onBack, onHome,
}: { learnerId: string; onBack: () => void; onHome: () => void }) {
  const [puzzle, setPuzzle] = useState<Mystery | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [guesses, setGuesses] = useState<string[]>([])
  const [typing, setTyping] = useState('')
  const [clues, setClues] = useState(0)
  const [note, setNote] = useState<string | null>(null)
  const [shake, setShake] = useState(false)

  const load = useCallback(() => {
    setError(null)
    setPuzzle(null)
    setGuesses([])
    setTyping('')
    setClues(0)
    setNote(null)
    api.mystery(learnerId).then(setPuzzle).catch(setError)
  }, [learnerId])
  useEffect(load, [load])

  const ready = puzzle && puzzle.ready ? puzzle : null
  const won = Boolean(ready && guesses.includes(ready.answer))
  const lost = Boolean(ready && !won && guesses.length >= ready.tries)
  const over = won || lost

  // Three misses and the meaning shows by itself.
  useEffect(() => {
    if (ready && !over && guesses.length >= 3 && clues === 0) setClues(1)
  }, [ready, over, guesses.length, clues])

  // The end: the word is always said and shown, whichever way it went.
  useEffect(() => {
    if (!ready || !over) return
    if (won) { sfx.correct(); window.setTimeout(() => sfx.finished(), 450) }
    else sfx.notYet()
    window.setTimeout(() => say(ready.word), 600)
  }, [over, won, ready])

  const onLetter = useCallback((l: string) => {
    if (!ready || over) return
    setNote(null)
    setTyping((t) => (t.length < ready.length ? t + l : t))
  }, [ready, over])

  const onBackKey = useCallback(() => {
    if (over) return
    setNote(null)
    setTyping((t) => t.slice(0, -1))
  }, [over])

  const onEnter = useCallback(() => {
    if (!ready || over) return
    if (typing.length < ready.length) {
      setNote(`It needs ${ready.length} letters.`)
      setShake(true)
      window.setTimeout(() => setShake(false), 400)
      return
    }
    sfx.tap()
    setGuesses((g) => [...g, typing])
    setTyping('')
  }, [ready, over, typing])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!puzzle) return <Loading what="Thinking of a word" />
  if (!ready) {
    return (
      <Page width={520}>
        <BackRow onBack={onBack} />
        <Empty emoji="🔍" title="Not yet" line={(puzzle as { message: string }).message} />
      </Page>
    )
  }

  const keyMarks: Record<string, KeyMark> = {}
  for (const g of guesses) {
    score(g, ready.answer).forEach((m, i) => {
      const l = g[i]!
      if (!keyMarks[l] || RANK[m] > RANK[keyMarks[l]!]) keyMarks[l] = m
    })
  }

  const rows = Array.from({ length: ready.tries }, (_, i) => {
    if (i < guesses.length) return { text: guesses[i]!, marks: score(guesses[i]!, ready.answer) }
    if (i === guesses.length && !over) return { text: typing, marks: null }
    return { text: '', marks: null }
  })

  return (
    <Page width={520}>
      <BackRow onBack={onBack} />
      <h1 style={{ margin: '4px 0 2px', fontSize: 28, color: theme.text }}>Mystery word</h1>
      <p style={{ margin: '0 0 14px', fontSize: 16, color: theme.muted }}>
        One of your words, with {ready.length} letters. Find it in {ready.tries} tries.
      </p>

      {/* the board */}
      <div
        role="grid"
        aria-label="Your guesses"
        style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', marginBottom: 12 }}
      >
        {rows.map((row, r) => (
          <div
            key={r}
            role="row"
            className={shake && r === guesses.length ? 'ws-shake' : undefined}
            style={{
              display: 'grid', gap: 6, width: '100%', maxWidth: ready.length * 58,
              gridTemplateColumns: `repeat(${ready.length}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: ready.length }, (_, c) => {
              const letter = row.text[c] ?? ''
              const mark = row.marks?.[c]
              const colors = mark ? MARK_COLORS[mark] : null
              return (
                <div
                  key={c}
                  role="gridcell"
                  aria-label={letter ? `${letter}${colors ? `, ${colors.label}` : ''}` : 'empty'}
                  style={{
                    aspectRatio: '1 / 1', display: 'grid', placeItems: 'center',
                    borderRadius: radius.sm, fontSize: 'clamp(20px, 6vw, 28px)', fontWeight: 800,
                    background: colors ? colors.bg : theme.card,
                    color: colors ? colors.ink : theme.text,
                    border: colors ? 'none' : `2px solid ${letter ? theme.text2 : theme.border}`,
                    transition: 'background 0.25s',
                  }}
                >
                  {letter}
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {note ? (
        <p role="status" style={{ textAlign: 'center', margin: '0 0 10px', color: theme.orangeDark, fontWeight: 700 }}>
          {note}
        </p>
      ) : null}

      {over ? (
        <Card style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{ display: 'grid', placeItems: 'center', marginBottom: 6 }}>
            <Mascot size={84} mood={won ? 'cheer' : 'wonder'} />
          </div>
          <div style={{ fontSize: 17, color: theme.muted, fontWeight: 700 }}>
            {won ? 'You found it!' : 'The word was'}
          </div>
          <button
            type="button"
            onClick={() => say(ready.word)}
            aria-label={`Hear ${ready.word}`}
            style={{
              margin: '4px 0 6px', fontSize: 34, fontWeight: 800, color: theme.greenDarker,
              background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <Trace id="T3.display_form">{ready.word}</Trace> 🔊
          </button>
          <p style={{ margin: '0 0 6px', fontSize: 17, color: theme.text2, lineHeight: 1.5 }}>
            <Trace id="T4.definition">{ready.definition}</Trace>
          </p>
          {ready.full_sentence ? (
            <p style={{ margin: '0 0 14px', fontSize: 15, color: theme.muted, fontStyle: 'italic' }}>
              <Trace id="T4.example_sentence">“{ready.full_sentence}”</Trace>
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button tone="child" onClick={load}>Another word</Button>
            <Button tone="ghost" onClick={onBack}>Other games</Button>
          </div>
        </Card>
      ) : (
        <>
          <ClueBox ready={ready} clues={clues} onMore={() => { sfx.tap(); setClues((n) => n + 1) }} />
          <LetterKeyboard
            onLetter={onLetter}
            onBack={onBackKey}
            onEnter={onEnter}
            marks={keyMarks}
            enterLabel="Try"
          />
          <Legend />
        </>
      )}
      <style>{`
        @keyframes ws-shake-kf { 0%,100% { transform: translateX(0) } 25% { transform: translateX(-6px) } 75% { transform: translateX(6px) } }
        .ws-shake { animation: ws-shake-kf 0.35s ease-in-out; }
        @media (prefers-reduced-motion: reduce) { .ws-shake { animation: none } }
      `}</style>
    </Page>
  )
}

function ClueBox({
  ready, clues, onMore,
}: { ready: Extract<Mystery, { ready: true }>; clues: number; onMore: () => void }) {
  const canMore = clues === 0 || (clues === 1 && Boolean(ready.sentence))
  return (
    <div style={{
      background: theme.greenPale, borderRadius: radius.lg, padding: '10px 14px',
      marginBottom: 12, minHeight: TAP_MIN,
    }}>
      {clues >= 1 ? (
        <p style={{ margin: '2px 0', fontSize: 16, color: theme.text2, lineHeight: 1.45 }}>
          <strong>Clue:</strong>{' '}
          <Trace id="T4.definition">{ready.definition}</Trace>{' '}
          <button type="button" onClick={() => say(ready.definition)} aria-label="Read the clue out loud"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>🔊</button>
        </p>
      ) : null}
      {clues >= 2 && ready.sentence ? (
        <p style={{ margin: '6px 0 2px', fontSize: 15, color: theme.muted, fontStyle: 'italic' }}>
          <Trace id="T4.example_sentence">“{ready.sentence}”</Trace>
        </p>
      ) : null}
      {canMore ? (
        <button type="button" onClick={onMore} style={{
          background: 'transparent', border: 'none', color: theme.greenDark, fontWeight: 800,
          fontSize: 15, cursor: 'pointer', padding: '6px 0', minHeight: 32, fontFamily: 'inherit',
        }}>
          {clues === 0 ? '💡 Need a clue?' : '💡 Another clue'}
        </button>
      ) : null}
    </div>
  )
}

function Legend() {
  return (
    <div style={{
      display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap',
      marginTop: 12, fontSize: 13, color: theme.muted,
    }}>
      {(Object.keys(MARK_COLORS) as KeyMark[]).map((m) => (
        <span key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: MARK_COLORS[m].bg }} />
          {MARK_COLORS[m].label}
        </span>
      ))}
    </div>
  )
}

/** The way back, pinned top-left like every screen's (see ExitButton). */
export function BackRow({ onBack, label = '← Games' }: { onBack: () => void; label?: string }) {
  return <ExitButton onExit={onBack} label={label} />
}
